import { Fragment } from "react";
import styled from "styled-components";
import { CgEye } from "react-icons/cg";
import { MdEdit } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Chip, Grid, IconButton, Tooltip } from "@mui/material";

import pages from "../../../constants/pages";
import { Search } from "../../../components";
import MuiTable from "../../../components/MuiTable";
import { colors } from "../../css components/Style";
import SortBy from "./superadmin list components/SortBy";
import Categorize from "./superadmin list components/Categorize";
import AccountCategorize from "./superadmin list components/AccountCategorize";
import { capitalCase } from "change-case-all";
import BrandFilter from "./superadmin list components/BrandFilter";

const Icon = styled(IconButton)`
  border: 1px solid ${colors.purple}!important;
  color: ${colors.purple}!important;
  height: 35px;
  width: 35px;
  margin: 0 5px;
  padding: 0 5px;
  &:hover {
    border: 1px solid ${colors.purple}!important;
    box-shadow: rgba(99, 99, 99, 0.2) 0px 2px 8px 0px;
  }
`;

export default function List({
  campaigns,
  component,
  categorize,
  setPage,
  setSearchKey,
  loading,
  setCategorize,
  sortBy,
  setSortBy,
  accountCategorize,
  setAccountCategorize,
  brandList,
  setBrandFilter,
  filterBrand,
}) {
  const navigate = useNavigate();
  const { role } = useSelector((state) => state.memberDetails);

  let columns = [
    { name: "campaign_name", label: "Campaign Name" },
    ...(role === "SUPER_ADMIN"
      ? [
          { name: "platform_source", label: "Platform Source" },
          {
            name: "brand_name",
            label: "Brand Name",
          },
          {
            name: "agency_name",
            label: "Agency Name",
          },
        ]
      : []),

    {
      name: "camapign_period",
      label: "Campaign Period",
    },

    { name: "campaignStatus", label: "Status" },
    {
      name: "action",
      label: "Action",
    },
  ];

  const renderCell = (col, value, row) => {
    if (col.name === "budget") {
      return row.min_budget + "-" + row.max_budget;
    }

    if (col.name === "brand_name") {
      return (
        <Tooltip title={row.brand?.agency ? row.brand?.agency : "Standalone"}>
          {row.brand.name}
        </Tooltip>
      );
    }

    if (col.name === "agency_name") {
      return row.brand?.agency ? (
        row.brand?.agency
      ) : (
        <span style={{ display: "flex", justifyContent: "center" }}>-</span>
      );
    }

    if (col.name === "camapign_period") {
      return (
        convertDate(row.start_duration) + " - " + convertDate(row.end_duration)
      );
    }

    if (col.name === "platform_source") {
      return row.partner_name
        ? `${value} / Partner - ${row.partner_name} `
        : value;
    }

    if (col.name === "campaignStatus") {
      let color;
      let background;
      if (
        value === "REQUESTED" ||
        value === "CREATED" ||
        value === "Approval Requested" ||
        value === "APPROVAL_REQUESTED" ||
        value === "LIVE_REQUESTED"
      ) {
        color = colors.warning;
        background = colors.lightWarning;
      }

      if (
        value === "OFFER_ACCEPTED" ||
        value === "ACTIVE" ||
        value === "ACCEPTED" ||
        value === "PARTIAL_ACCEPTED" ||
        value === "SIZE_READY" ||
        value === "READY"
      ) {
        color = colors.success;
        background = colors.lightSuccess;
      }

      if (
        value === "INITIATED" ||
        value === "Sizing in Progress" ||
        value === "SIZING_IN_PROGRESS"
      ) {
        color = colors.purple;
        background = colors.lightPurple;
      }

      if (
        value === "REJECTED" ||
        value === "Expired" ||
        value === "FAILED" ||
        value === "MISSED" ||
        value === "PARTIAL_REJECTED"
      ) {
        color = colors.error;
        background = colors.lightError;
      }

      return (
        <Chip
          style={{
            fontSize: "13px",
            backgroundColor: background,
            color: color,
          }}
          label={capitalCase(value)}
        />
      );
    }

    if (col.name === "action") {
      return (
        <div>
          <Tooltip title="View Campaign">
            <Icon>
              <CgEye
                onClick={() => handleRedirection(row, "view")}
                style={{ height: "20px", width: "20px" }}
              />
            </Icon>
          </Tooltip>

          {component === "" &&
            row.status !== "ACTIVE" &&
            row.status !== "INITIATED" &&
            row.isEditable && (
              <Tooltip title="Edit Campaign">
                <Icon onClick={() => handleRedirection(row, "edit")}>
                  <MdEdit style={{ height: "20px", width: "20px" }} />
                </Icon>
              </Tooltip>
            )}
        </div>
      );
    }

    return value; // default
  };

  return (
    <Grid container spacing={8}>
      <Grid size={12} />
      <Grid size={12}>
        <MuiTable
          loading={loading}
          components={[
            <ToolBar
              filterBrand={filterBrand}
              setBrandFilter={setBrandFilter}
              brandList={brandList}
              categorize={categorize}
              setCategorize={setCategorize}
              setPage={setPage}
              setSearchKey={setSearchKey}
              setSortBy={setSortBy}
              sortBy={sortBy}
              accountCategorize={accountCategorize}
              setAccountCategorize={setAccountCategorize}
            />,
          ]}
          columns={columns}
          renderCell={renderCell}
          data={campaigns}
        />
      </Grid>
    </Grid>
  );

  function convertDate(d) {
    let date = new Date(d);

    return (
      date.getDate() + "." + (date.getMonth() + 1) + "." + date.getFullYear()
    );
  }

  function handleRedirection(campaign, mode) {
    if (component === "request") {
      navigate(
        pages.campaignRequestDetails.route +
          campaign.sender_campaignId +
          "?brandID=" +
          campaign.sender_brandId +
          "&mode=" +
          mode
      );
    }

    if (component === "superadmin") {
      navigate(
        pages.superAdminCampaignDetails.route +
          campaign.campaign_id +
          "?brandID=" +
          campaign.brand_id +
          "&mode=" +
          mode,
        { state: { categorize: categorize } }
      );
    }

    if (component === "accepted") {
      navigate(
        pages.campaignAcceptedDetails.route +
          campaign.campaign_id +
          "?brandID=" +
          campaign.brand_id +
          "&mode=" +
          mode
      );
    }

    if (component === "") {
      navigate(
        pages.campaignDetails.route + campaign.campaign_id + "?mode=" + mode
      );
    }
  }
}

export function ToolBar({
  setPage,
  setSearchKey,
  categorize,
  sortBy,
  setSortBy,
  setCategorize,
  accountCategorize,
  setAccountCategorize,
  setBrandFilter,
  brandList,
  filterBrand,
}) {
  const { role } = useSelector((state) => state.memberDetails);

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        justifyContent: "space-between",
      }}
    >
      <div style={{ display: "flex", gap: "10px" }}>
        <Search setPage={setPage} setSearchKey={setSearchKey} />

        {role === "SUPER_ADMIN" && (
          <Fragment>
            <Categorize
              categorize={categorize}
              setCategorize={setCategorize}
              setPage={setPage}
            />

            <AccountCategorize
              accountCategorize={accountCategorize}
              setAccountCategorize={setAccountCategorize}
              setPage={setPage}
            />

            <BrandFilter
              brandList={brandList}
              setBrandFilter={setBrandFilter}
              setPage={setPage}
              filterBrand={filterBrand}
            />

            <SortBy
              setPage={setPage}
              sortBy={sortBy}
              setSortBy={setSortBy}
              categorize={categorize}
            />
          </Fragment>
        )}
      </div>
    </div>
  );
}
